import React, { useEffect, useMemo, useState } from 'react';
import { MdCheck, MdClose, MdGavel } from 'react-icons/md';
import { Button, Modal } from '@aibuild/ui';
import type { GateNodeData, ProcessNode } from './graphTypes';
import type { GateDecisionValue } from '@api/processRuns';
import type { ProcessArtifactDto } from '@api/processes';
import { useOrgMembers } from '../hooks/useOrgMembers';
import { useGroups } from '../hooks/useGroups';
import ArtifactDownloadList from './nodes/ArtifactDownloadList';
import {
  Body,
  CommentField,
  FieldLabel,
  NodeContext,
  NodeInstructions,
  NodeLabel,
  ReviewFiles,
  RoleHint,
} from './GateDecisionDialog.styled';

export interface GateDecisionDialogProps {
  /** When non-null, the dialog is open and points at this gate node. */
  gate: { readonly nodeId: string; readonly iteration: number } | null;
  /** Live editor nodes -- looked up to render the gate's instructions. */
  nodes: readonly ProcessNode[];
  /** True while the parent's decide mutation is in flight. */
  isDeciding: boolean;
  /** Called with the user's choice. Throws on backend error so the parent decides whether to keep the dialog open. */
  onDecide: (decision: GateDecisionValue, comment?: string) => Promise<void>;
  /** Closes the dialog without deciding. */
  onClose: () => void;
  /**
   * Optional decision pre-selected by the user from an in-canvas
   * affordance (e.g. clicking the Approved / Rejected pill on the gate
   * node). Used only as a visual hint — the user still has to confirm
   * by clicking the matching button. Lets the reopen-after-Decide-later
   * flow surface the user's intent in the modal copy.
   */
  prefillDecision?: GateDecisionValue | null;
  /** Files the gate is reviewing, i.e. the upstream output mirrored onto the gate. */
  reviewArtifacts?: readonly ProcessArtifactDto[];
  /** The dialog mounts outside the editor context, so the URL refresher is passed in. */
  refreshArtifactUrl?: (artifactId: string) => Promise<string>;
  dataTestId?: string;
}

const SUBTITLE_FOR_PREFILL: Record<GateDecisionValue | 'none', string> = {
  approved: 'Confirm your approval, or change your mind below.',
  rejected: 'Confirm your rejection, or change your mind below.',
  none: 'A test run is paused waiting for your decision.',
};

/**
 * Render the reviewer-scope hint shown beneath the gate instructions.
 *
 * <p>Distinguishes four cases:</p>
 * <ul>
 *   <li><b>No restriction</b> — {@code reviewerIds} is empty/undefined.
 *       The permissive "Anyone in your organization can approve."
 *       message is shown.</li>
 *   <li><b>Loading</b> — {@code reviewerIds} is non-empty but the org
 *       member directory hasn't resolved yet ({@code reviewerNames}
 *       is {@code null}). We must not advertise "Anyone" — restrictions
 *       are still enforced server-side regardless of what the UI says.</li>
 *   <li><b>Restricted but unresolved names</b> — directory loaded but
 *       none of the reviewer IDs match (e.g. all referenced members
 *       were removed). Show a safe generic so the user knows the gate
 *       is gated to specific people without claiming "Anyone".</li>
 *   <li><b>Restricted with names</b> — the canonical "Assigned to:
 *       Alice, Bob" line.</li>
 * </ul>
 */
function renderRoleHint(
  reviewerIds: readonly string[] | undefined,
  reviewerNames: readonly string[] | null,
  dataTestId: string,
): React.ReactElement {
  if (!reviewerIds || reviewerIds.length === 0) {
    return (
      <RoleHint data-testid={`${dataTestId}__role-hint-everyone`}>
        Anyone in your organization can approve.
      </RoleHint>
    );
  }
  if (reviewerNames === null) {
    return (
      <RoleHint data-testid={`${dataTestId}__role-hint-loading`}>
        Loading reviewers…
      </RoleHint>
    );
  }
  if (reviewerNames.length === 0) {
    return (
      <RoleHint data-testid={`${dataTestId}__role-hint-restricted`}>
        Assigned to specific reviewers.
      </RoleHint>
    );
  }
  return (
    <RoleHint data-testid={`${dataTestId}__role-hint`}>
      Assigned to: {reviewerNames.join(', ')}
    </RoleHint>
  );
}

function renderGroupHint(
  groupName: string | null,
  groupExists: boolean,
  dataTestId: string,
): React.ReactElement {
  if (groupName) {
    return (
      <RoleHint data-testid={`${dataTestId}__role-hint-group`}>
        Assigned to group: {groupName}
      </RoleHint>
    );
  }
  if (!groupExists) {
    return (
      <RoleHint data-testid={`${dataTestId}__role-hint-group-deleted`}>
        The assigned group has been deleted. Ask an admin to reconfigure this
        gate&rsquo;s approver.
      </RoleHint>
    );
  }
  return (
    <RoleHint data-testid={`${dataTestId}__role-hint-group-loading`}>
      Loading group…
    </RoleHint>
  );
}

/**
 * Modal that captures a reviewer's gate decision (approve / reject) plus
 * an optional comment.
 *
 * <p>Mounted only when {@link GateDecisionDialogProps.gate} is non-null
 * so the surrounding editor doesn't pay the render cost when no gate is
 * waiting. Reads the gate node's metadata from the live React Flow
 * nodes so reviewer instructions reflect the current draft, not a
 * snapshot from the run start.</p>
 *
 * <p>Reviewer restriction is enforced server-side; the dialog surfaces
 * designated reviewer names as a hint so unassigned users know their
 * submission would 403.</p>
 */
const GateDecisionDialog: React.FC<GateDecisionDialogProps> = ({
  gate,
  nodes,
  isDeciding,
  onDecide,
  onClose,
  prefillDecision,
  reviewArtifacts = [],
  refreshArtifactUrl,
  dataTestId = 'gate-decision-dialog',
}) => {
  const node = gate ? nodes.find((n) => n.id === gate.nodeId) : undefined;
  const data = node?.data as GateNodeData | undefined;
  const [comment, setComment] = useState('');
  const { data: orgMembers } = useOrgMembers();
  const { data: orgGroups } = useGroups();

  const isGroupApprover = data?.approverType === 'GROUP';
  const groupName = useMemo(() => {
    if (!isGroupApprover || !data?.reviewerGroupId || !orgGroups) return null;
    return orgGroups.find((g) => g.id === data.reviewerGroupId)?.name ?? null;
  }, [isGroupApprover, data?.reviewerGroupId, orgGroups]);
  // Resolved (directory loaded) AND not found ⇒ the group was deleted.
  const groupExists = !isGroupApprover || groupName !== null || orgGroups === undefined;

  const reviewerNames = useMemo(() => {
    const ids = data?.reviewerIds;
    if (!ids || ids.length === 0 || !orgMembers) return null;
    const idSet = new Set(ids);
    return orgMembers
      .filter((m) => idSet.has(m.id))
      .map((m) => m.displayName || m.email);
  }, [data?.reviewerIds, orgMembers]);

  // Reset the comment whenever the dialog opens for a different gate
  // (a new node, or the same node on a fresh iteration). The optional
  // chaining in the deps array deliberately tracks identity changes
  // through `gate` becoming null and back -- silenced exhaustive-deps
  // because adding the whole `gate` object would trigger spurious
  // resets on unrelated re-renders.
  useEffect(() => {
    if (gate) setComment('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate?.nodeId, gate?.iteration]);

  const handleDecide = async (decision: GateDecisionValue) => {
    try {
      await onDecide(decision, comment.trim() || undefined);
    } catch {
      // Parent surfaces the error via snackbar; keep the dialog open so
      // the user can retry / cancel.
    }
  };

  // When the user opened the modal via the in-canvas pill we surface
  // their intent in the subtitle so the next click feels like a
  // confirmation rather than a fresh choice. The matching button is
  // still the same Approve / Reject control.
  const subtitle = SUBTITLE_FOR_PREFILL[prefillDecision ?? 'none'];

  // Rejecting requires a comment because that comment becomes the
  // rejection cause downstream nodes consume in their input brief.
  // Approving never requires one.
  const isCommentEmpty = comment.trim().length === 0;
  const rejectDisabled = isDeciding || isCommentEmpty;

  return (
    <Modal
      open={Boolean(gate)}
      onClose={isDeciding ? () => undefined : onClose}
      title="Gate review"
      subtitle={subtitle}
      icon={<MdGavel />}
      size="md"
      dataTestId={dataTestId}
      hideCloseButton={isDeciding}
      disableEscape={isDeciding}
      disableBackdropClose={isDeciding}
      footer={
        <>
          <Button
            variant="text"
            onClick={onClose}
            disabled={isDeciding}
            dataTestId={`${dataTestId}__cancel`}
          >
            Decide later
          </Button>
          <Button
            variant="text"
            onClick={() => void handleDecide('rejected')}
            disabled={rejectDisabled}
            leadingIcon={<MdClose />}
            dataTestId={`${dataTestId}__reject`}
          >
            Reject
          </Button>
          <Button
            variant="filled"
            onClick={() => void handleDecide('approved')}
            disabled={isDeciding}
            leadingIcon={<MdCheck />}
            dataTestId={`${dataTestId}__approve`}
          >
            {isDeciding ? 'Deciding…' : 'Approve'}
          </Button>
        </>
      }
    >
      <Body data-testid={`${dataTestId}__body`}>
        <NodeContext>
          <NodeLabel data-testid={`${dataTestId}__node-label`}>
            {data?.label ?? 'Gate'}
          </NodeLabel>
          {data?.reviewInstructions ? (
            <NodeInstructions data-testid={`${dataTestId}__instructions`}>
              {data.reviewInstructions}
            </NodeInstructions>
          ) : (
            <NodeInstructions>
              No review instructions provided.
            </NodeInstructions>
          )}
          {isGroupApprover
            ? renderGroupHint(groupName, groupExists, dataTestId)
            : renderRoleHint(data?.reviewerIds, reviewerNames, dataTestId)}
        </NodeContext>
        {reviewArtifacts.length > 0 && (
          <ReviewFiles>
            <FieldLabel as="div">Files to review</FieldLabel>
            <ArtifactDownloadList
              artifacts={reviewArtifacts}
              dataTestId={`${dataTestId}__review-files`}
              refreshArtifactUrl={refreshArtifactUrl}
            />
          </ReviewFiles>
        )}
        <FieldLabel htmlFor="gate-comment">
          Comment (required to reject)
        </FieldLabel>
        <CommentField
          id="gate-comment"
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Notes for the run log…"
          disabled={isDeciding}
          data-testid={`${dataTestId}__comment`}
        />
        <RoleHint data-testid={`${dataTestId}__reject-hint`}>
          Rejecting requires a comment — it becomes the cause shown to
          downstream nodes. Approving doesn&rsquo;t.
        </RoleHint>
      </Body>
    </Modal>
  );
};

export default GateDecisionDialog;
